import { authActions } from 'store/user'
import { actions as notyActions } from 'layouts/ErrorBox'
import { loadingActions } from 'store/loader'
import { HPI_HARDWARE_ID_HEADER_NAME } from 'utils/constants'
import { getHpiHardwareIdHeaderValue } from 'utils/hardwareId'
import { logSentryEvent, SENTRY_MESSAGES } from './sentry'

const _getDefaultMessages = (status, strings) => {
  switch (status) {
    case 401:
      return strings.error_401
    case 403:
      return strings.error_403
    case 404:
      return strings.error_404
    case 500:
    default:
      return strings.error_500
  }
}

// Track currently displayed error messages to prevent duplicates
const displayedMessages = new Set()
let lastConsentNotificationTime = 0
const CONSENT_NOTIFICATION_COOLDOWN = 3000 // 3 seconds cooldown for consent notifications

// default fail behavior is to display _defaultMessages
// customFail will replace all default behavior
const _defaultFail = (
  response,
  resContent,
  customFail,
  failMessage,
  skipNotyFail,
  suppressLogoutOnAuthError = false
) => {
  const content = JSON.parse(resContent)
  return (dispatch, getState) => {
    const { strings, location, errorBox } = getState()
    const pathname = location?.pathname || ''
    let errMessage =
      failMessage || _getDefaultMessages(response.status, strings)
    errMessage =
      errMessage ||
      content.message ||
      `${response.status} Error: ${response.statusText}.`

    const isBlockingEConsent =
      response.status === 403 &&
      content.message &&
      content.message.toLowerCase().replace(/\s+/g, '').includes('blockingeconsentpresent')

    // Check if this is a consent-related error message
    const isConsentError =
      response.status === 403 &&
      (isBlockingEConsent || errMessage === strings.error_403)

    // Skip notifications for blocking consent errors on Activities/Visits tabs
    // These tabs already display inline consent prompts
    const shouldSkipConsentNotification =
      isConsentError &&
      (pathname === '/my-activities' || pathname === '/visits')

    // Check if the same message is already displayed
    // Check both the errorBox state and our tracking Set
    const isDuplicateMessage =
      (errorBox?.noty && errorBox?.options?.text === errMessage) ||
      displayedMessages.has(errMessage)

    // For Profile tab, only show one consent notification at a time with cooldown
    const isOnProfileTab = pathname === '/profile'
    const now = Date.now()
    const shouldThrottleConsentNotification =
      isOnProfileTab &&
      isConsentError &&
      (now - lastConsentNotificationTime < CONSENT_NOTIFICATION_COOLDOWN)

    const { caregiver } = getState()
    const isSwitchingParticipant = !!(caregiver && caregiver.switching)

    if (response.status === 401) {
      logSentryEvent({
        message: SENTRY_MESSAGES.tokenFailure,
        level: 'error',
        tags: {
          flow: 'auth',
          action: 'token_failure',
          'http.status': response.status,
        },
      })
    }

    logSentryEvent({
      message: 'API request failed',
      level: 'error',
      tags: {
        'http.status': response.status,
        'error.kind': isConsentError ? 'consent' : 'generic'
      },
      contexts: {
        http: {
          url: response.url || '',
          method: response?.method || 'UNKNOWN'
        },
        location: {
          path: pathname
        }
      },
      breadcrumb: {
        category: 'http',
        message: 'API request failed',
        level: 'error'
      }
    })
    if (!suppressLogoutOnAuthError && !isSwitchingParticipant) {
      if (response.status === 401 || (response.status === 403 && !isBlockingEConsent)) {
        dispatch(authActions.clearSession())
        dispatch(
          notyActions.showError({
            text: `${response.status === 401
              ? _getDefaultMessages(401, strings)
              : _getDefaultMessages(403, strings)
              }`,
            timeout: 5000,
            killer: true
          })
        )
      }
    }
    if (customFail) {
      dispatch(customFail(response, content.message))
    } else if (!skipNotyFail) {
      // Skip notification if:
      // 1. It's a consent error on Activities/Visits tabs (they have inline prompts)
      // 2. The same message is already displayed
      // 3. It's a consent notification on Profile tab within cooldown period
      if (shouldSkipConsentNotification || isDuplicateMessage || shouldThrottleConsentNotification) {
        return
      }

      // Track the displayed message
      displayedMessages.add(errMessage)

      // Track consent notification time for Profile tab
      if (isOnProfileTab && isConsentError) {
        lastConsentNotificationTime = now
      }

      // Clear the message from tracking after timeout (slightly longer than notification timeout)
      setTimeout(() => {
        displayedMessages.delete(errMessage)
      }, 2500) // Slightly longer than 2000ms notification timeout

      dispatch(notyActions.showError({ text: errMessage, timeout: 2000 }))
    }
  }
}

// default success behavior
const _getDefaultSuccess = () => {
  // request() expects `success` to be a callable function.
  // Some call sites omit `success`, so we provide a safe no-op default.
  return () => true
}

// validate the session and returns a promise

const handleInvalidSession = (dispatch, err) => {
  logSentryEvent({
    message: SENTRY_MESSAGES.appSessionInvalid,
    level: 'error',
    tags: {
      flow: 'auth',
      action: 'app_session_invalid',
    },
    error: err,
  })
  dispatch(authActions.clearSession())
  dispatch(
    notyActions.showError({ text: err.message, timeout: 5000, killer: true })
  )
}

export const validateSession = (dispatch, user, callback, strings) => {
  let errMessage
  return new Promise(resolve => {
    if (!user.token || !user.exp) {
      logSentryEvent({
        message: SENTRY_MESSAGES.missingAuthToken,
        level: 'error',
        tags: {
          flow: 'auth',
          action: 'missing_auth_token',
        },
      })
      errMessage = _getDefaultMessages(401, strings)
    } else if (user.exp * 1000 < Date.now()) {
      logSentryEvent({
        message: SENTRY_MESSAGES.tokenFailure,
        level: 'error',
        tags: {
          flow: 'auth',
          action: 'token_failure',
        },
      })
      errMessage = strings.error_session_expired
    }
    if (errMessage) throw new Error(errMessage)
    else resolve()
  })
    .then(callback)
    .catch(err => handleInvalidSession(dispatch, err))
}

const request = ({
  method = 'GET',
  url,
  body,
  headerOptions,
  contentType = 'application/json',
  success,
  skipNotySuccess,
  skipNotyFail = false,
  fail,
  failMessage,
  successMessage,
  catchMessage,
  forceLoader = false,
  authRequired = true,
  resType = 'json', // currently supports only blob or json responses
  hasLoader = false,
  loadingKey,
  bypassEnforcedLang = false,
  suppressLogoutOnAuthError = false,
  skipNetworkErrorReporting = false,
  onFailLog,
}) => (dispatch, getState) => {
  if (hasLoader) {
    if (loadingKey) {
      dispatch(loadingActions.startLoader(true, loadingKey))
    } else {
      dispatch(loadingActions.startLoader(forceLoader))
    }
  }
  const resolveLoader = () => {
    if (hasLoader) {
      if (loadingKey) {
        dispatch(loadingActions.stopLoader(true, loadingKey))
      } else {
        dispatch(loadingActions.stopLoader(forceLoader))
      }
    }
  }
  const { user, strings } = getState()
  const enforcedLanguage =
    user?.study?.config?.enforced_language?.default_language

  let isZip = false
  const callback = () => {
    // track response
    let response

    // establish default success behavior (print success message if provided)
    const successHandler =
      typeof success === 'function'
        ? success
        : _getDefaultSuccess

    // setup headers
    const headers = {
      Authorization: user.token,
    }
    const headerUsername = user?.username || user?.name || user?.email
    // Third-party APIs (e.g. Applanga) must list every custom header in
    // Access-Control-Allow-Headers; omit ours when the host does not allow it.
    let omitHardwareIdHeader = false
    if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
      try {
        omitHardwareIdHeader = new URL(url).hostname === 'api.applanga.com'
      } catch {
        omitHardwareIdHeader = false
      }
    }
    if (!omitHardwareIdHeader) {
      headers[HPI_HARDWARE_ID_HEADER_NAME] =
        getHpiHardwareIdHeaderValue(headerUsername)
    }
    if (enforcedLanguage && !bypassEnforcedLang) {
      headers['accept-language'] = enforcedLanguage
    }
    if (contentType) headers['Content-Type'] = contentType
    Object.assign(headers, headerOptions || {})

    if (contentType === 'multipart/form-data') {
      delete headers['Content-Type']
    }

    // make fetch request
    return fetch(url, { method, headers, body })
      .then(res => {
        response = res
        if (res.ok) {
          if (res.url.includes('.zip')) isZip = true
          if (!isZip) {
            if (resType === 'json') return res.json()
            if (resType === 'blob') return res.blob()
          }
          return Promise.resolve(res.blob())
        }
        return res.text()
      })
      .then(resContent => {
        let nextStep
        if (response.ok) {
          if (!skipNotySuccess && successMessage)
            dispatch(
              notyActions.showSuccess({
                text: successMessage,
                timeout: 2000,
              }),
            )
          nextStep = successHandler(resContent)
        } else {
          if (typeof onFailLog === 'function') {
            try {
              onFailLog(response, resContent)
            } catch (_) {
              /* logging must never break requests */
            }
          }
          nextStep = dispatch(
            _defaultFail(
              response,
              resContent,
              fail,
              failMessage,
              skipNotyFail,
              suppressLogoutOnAuthError,
            ),
          )
        }
        resolveLoader()
        return nextStep || true // return default true to handle conditional promise chaining
      })
      .catch(err => {
        if (catchMessage)
          dispatch(
            notyActions.showError({ text: catchMessage, timeout: 2000 }),
          )

        if (typeof onFailLog === 'function') {
          try {
            onFailLog(undefined, err)
          } catch (_) {
            /* logging must never break requests */
          }
        }

        if (!skipNetworkErrorReporting) {
          logSentryEvent({
            message: 'Network error',
            level: 'error',
            tags: {
              'error.kind': 'network'
            },
            contexts: {
              http: {
                url: url || '',
                method
              }
            },
            error: err
          })
        }

        resolveLoader()
        console.log(err)
      })
  }
  // During participant switching, in-flight requests may use a stale token.
  // Skip session validation to avoid triggering a logout mid-switch.
  const { caregiver } = getState()
  const isSwitchingParticipant = !!(caregiver && caregiver.switching)
  if (!authRequired || isSwitchingParticipant) return callback()
  return validateSession(dispatch, user, callback, strings)
}

export default request
